'use client'

import { parseAsString, useQueryState } from 'nuqs'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { api } from '@/convex/_generated/api'
import { useAuthedQuery } from '@/hooks/use-authed-query'
import { isConvexOrgReady, useConvexOrgId } from '@/hooks/use-convex-org-id'
import { mintChatId } from './local-chat-ids'
import {
  adoptTabInto,
  type CanvasChatScope,
  type CanvasChatTab,
  capTabs,
  PANEL_OPEN_KEY,
  pruneForeignTabs,
  type ResolveActiveChatResult,
  readStoredActiveChat,
  readStoredString,
  readStoredTabs,
  resolveActiveChat,
  writeStoredActiveChat,
  writeStoredTabs,
} from './use-canvas-chats-utils'

export type { CanvasChatScope, CanvasChatTab } from './use-canvas-chats-utils'

export type UseCanvasChatsReturn = {
  openTabs: Array<CanvasChatTab>
  activeChatId: string | null
  setActiveChatId: (id: string | null) => void
  /**
   * Activate an ALREADY-OPEN tab from the strip. Real chats mirror into
   * `?chat=`; ephemeral `isNew` placeholders do NOT — mirroring one would
   * round-trip through the adopt effect and promote it to `isNew: false`,
   * which renamed a never-used tab to "Untitled" and suppressed the chat
   * thread's empty state (blank pane on re-select).
   */
  selectTab: (chatId: string) => void
  addTab: () => string
  openExistingChat: (chatId: string) => void
  closeTab: (chatId: string) => void
  markTabActive: (chatId: string) => void
  /**
   * Whether `chatId` is an open tab right now. Stable, and reads the latest
   * committed strip, so a long-lived store can ask it outside render.
   */
  isChatOpen: (chatId: string) => boolean
  /**
   * Make `chatId` the active chat WITHOUT mirroring it into the `?chat=`
   * URL param. Used when the active chat is already encoded in the path
   * (the full-page `/chat/<id>` surface) so we don't end up with a
   * redundant `/chat/<id>?chat=<id>`. Adds the tab if it isn't open yet.
   */
  adoptActiveChat: (chatId: string) => void
  /**
   * Drop any open tab whose chat belongs to a brand other than the active one,
   * given a `chatId → owning brandId` map. Brand-new ids absent from the map are
   * kept. Driven by `<ForeignTabPruner>` (which owns the `chatBrandMap` query).
   */
  pruneTabs: (brandMap: Record<string, string>) => void
  isLeftPanelOpen: boolean
  setIsLeftPanelOpen: (open: boolean) => void
}

const CHAT_QUERY_PARSER = parseAsString.withOptions({
  shallow: true,
  history: 'replace',
  clearOnDefault: true,
})

export function useCanvasChats(
  orgId: string,
  brandId?: string,
  scope?: CanvasChatScope
): UseCanvasChatsReturn {
  const [urlChatId, setUrlChatId] = useQueryState('chat', CHAT_QUERY_PARSER)

  // Brand-isolation gate for the `?chat=` URL param. When a stale / deep-linked
  // param points at a chat that belongs to a DIFFERENT brand than the active
  // one, clear it so the resolver falls back to the active brand's own chat —
  // a sibling brand's thread never renders on a workspace surface (/emails,
  // /audience, …). A brand-new (not-yet-persisted) chat id is NOT cleared (the
  // query returns false for a missing chat), and the full-page `/chat/<id>`
  // surface is unaffected (it doesn't use `?chat=`), so watching a
  // not-yet-active brand's extraction chat still works.
  // Org-ready gate (mirrors brand-context's listOrgBrands): during an org
  // switch's residual JWT window a `?chat=` param could fire this read with
  // the NEW org's args against the PREVIOUS org's token — NOT_AUTHORIZED
  // into OrgAuthSwitchBoundary. Held reads just defer the param cleanup.
  const convexOrgId = useConvexOrgId()
  const isUrlChatClaimedByOtherBrand = useAuthedQuery(
    api.chats.queries.chatClaimedByOtherBrand,
    urlChatId && brandId && isConvexOrgReady(convexOrgId, orgId)
      ? { chatId: urlChatId, orgId, brandId }
      : 'skip'
  )
  useEffect(() => {
    if (urlChatId && isUrlChatClaimedByOtherBrand === true) {
      void setUrlChatId(null)
    }
  }, [urlChatId, isUrlChatClaimedByOtherBrand, setUrlChatId])

  // Seed openTabs + activeChatId from a single resolver pass during initial
  // render. The URL is canonical across navigation, but tracking
  // `activeChatId` as local state too means the chat thread renders on the
  // FIRST paint when the URL was empty (resolver picked the last-active id
  // from per-scope localStorage). Without this seed, `activeChatId:
  // urlChatId` would stay null until the post-mount setUrlChatId mirror
  // takes effect — and the chat panel would render blank in between.
  // The ref guard ensures both useState initializers share one resolver
  // pass instead of each calling readStoredTabs/readStoredActiveChat/
  // resolveActiveChat independently on first render.
  const initialResolvedRef = useRef<ResolveActiveChatResult | null>(null)
  if (initialResolvedRef.current === null) {
    initialResolvedRef.current = resolveActiveChat({
      urlChatId,
      storedTabs: readStoredTabs(orgId, brandId, scope),
      storedLastActive: readStoredActiveChat(orgId, brandId, scope),
      generateChatId: () => mintChatId({ orgId, brandId }),
    })
  }
  const [openTabs, setOpenTabsRaw] = useState<Array<CanvasChatTab>>(
    initialResolvedRef.current.tabs
  )
  const [activeChatId, setActiveChatIdRaw] = useState<string | null>(
    initialResolvedRef.current.activeId
  )

  const [isLeftPanelOpen, setIsLeftPanelOpenRaw] = useState(
    () => readStoredString(PANEL_OPEN_KEY, 'true') === 'true'
  )

  const openTabsRef = useRef(openTabs)
  useEffect(() => {
    openTabsRef.current = openTabs
  }, [openTabs])

  const persistTabs = useCallback(
    (tabs: Array<CanvasChatTab>) => {
      setOpenTabsRaw(tabs)
      writeStoredTabs(orgId, brandId, tabs, scope)
    },
    [orgId, brandId, scope]
  )

  const setActiveChatId = useCallback(
    (id: string | null) => {
      setActiveChatIdRaw(id)
      void setUrlChatId(id ?? null)
      writeStoredActiveChat(orgId, brandId, id, scope)
    },
    [orgId, brandId, scope, setUrlChatId]
  )

  // Local-only active setter that updates state + lastActive storage but
  // skips mirroring into the URL. Used for ephemeral isNew tabs (fresh
  // tab on initial mount, addTab, closeTab last-tab fallback) so a
  // brand-new account refreshing /emails doesn't end up with a phantom
  // ?chat=<nanoid> that resolves to an "Untitled" tab on the next mount.
  // The URL is promoted later via markTabActive when the user submits.
  const setActiveChatIdLocal = useCallback(
    (id: string | null) => {
      setActiveChatIdRaw(id)
      writeStoredActiveChat(orgId, brandId, id, scope)
    },
    [orgId, brandId, scope]
  )

  // Initial sync after first render (covers the case where mount resolved a
  // tab/active id from localStorage but the URL was empty — mirror it back
  // into the URL with `history: 'replace'`). Also persists any appended tab
  // when URL had a chat id missing from stored tabs.
  //
  // CRITICAL: re-use `initialResolvedRef.current` instead of calling the
  // resolver again. `resolveActiveChat`'s fresh-tab branch generates a
  // nanoid; calling it twice would produce a different id than the one
  // already in `openTabs`, leaving `activeChatId` pointing at a tab that
  // doesn't exist and unmounting the chat thread (blank panel).
  const didInitRef = useRef(false)
  // biome-ignore lint/correctness/useExhaustiveDependencies: pre-Ultracite baseline — verify deps when touching this hook
  useEffect(() => {
    if (didInitRef.current) {
      return
    }
    didInitRef.current = true

    const result = initialResolvedRef.current
    if (!result) {
      return
    }

    if (result.appendedTab) {
      writeStoredTabs(orgId, brandId, result.tabs, scope)
      setOpenTabsRaw(result.tabs)
    }
    if (result.shouldMirrorToUrl) {
      void setUrlChatId(result.activeId)
    }
    setActiveChatIdRaw(result.activeId)
    writeStoredActiveChat(orgId, brandId, result.activeId, scope)
  }, [])

  // On scope (orgId/brandId/scope) change: reload per-scope storage and
  // re-resolve against the current URL `chat` param.
  const [prevScope, setPrevScope] = useState({ orgId, brandId, scope })
  if (
    prevScope.orgId !== orgId ||
    prevScope.brandId !== brandId ||
    prevScope.scope !== scope
  ) {
    setPrevScope({ orgId, brandId, scope })
    const stored = readStoredTabs(orgId, brandId, scope)
    const lastActive = readStoredActiveChat(orgId, brandId, scope)
    const result = resolveActiveChat({
      urlChatId,
      storedTabs: stored,
      storedLastActive: lastActive,
      generateChatId: () => mintChatId({ orgId, brandId }),
    })
    setOpenTabsRaw(result.tabs)
    setActiveChatIdRaw(result.activeId)
    if (result.appendedTab) {
      writeStoredTabs(orgId, brandId, result.tabs, scope)
    }
    if (result.shouldMirrorToUrl) {
      void setUrlChatId(result.activeId)
    }
    writeStoredActiveChat(orgId, brandId, result.activeId, scope)
  }

  // If the URL `chat` param ever points at a chat that's not in our tabs
  // (e.g. someone pasted a deep link mid-session, or the launcher/templates
  // remix pushed `?chat=<id>`), adopt it. `adoptTabInto` also consumes the
  // empty `isNew` placeholder the user was sitting on, so arriving at a real
  // chat from the fresh "New chat" seed doesn't strand an orphan tab. Doing
  // this in an effect — rather than only at mount — covers history navigation
  // / external links arriving after the hook is already mounted.
  useEffect(() => {
    if (!urlChatId) {
      return
    }
    const result = adoptTabInto({
      tabs: openTabsRef.current,
      activeChatId,
      chatId: urlChatId,
    })
    if (!result.changed) {
      return
    }
    persistTabs(result.tabs)
    writeStoredActiveChat(orgId, brandId, urlChatId, scope)
  }, [urlChatId, activeChatId, orgId, brandId, scope, persistTabs])

  // External URL changes (back/forward, deep link mid-session, another
  // surface calling setUrlChatId) drive local active state. Skip when the
  // URL was just cleared (urlChatId=null) so we don't briefly null out the
  // chat panel — last-active stays active until the user picks a new tab.
  useEffect(() => {
    if (!urlChatId) {
      return
    }
    setActiveChatIdRaw((current) =>
      current === urlChatId ? current : urlChatId
    )
    writeStoredActiveChat(orgId, brandId, urlChatId, scope)
  }, [urlChatId, orgId, brandId, scope])

  // ── Brand isolation for the TAB STRIP ─────────────────────────────────────
  // Open-tab lists are brand-scoped in localStorage, but full-page `/chat/<id>`
  // adoption and brand-switch races can write a SIBLING brand's chat into the
  // active brand's bucket — and nothing else prunes it, so the strip keeps
  // showing a foreign tab across reloads. `pruneTabs` drops any open tab whose
  // chat belongs to a DIFFERENT brand, given a `chatId → owning brandId` map.
  // Brand-new (not-yet-persisted) ids are ABSENT from the map and KEPT (a chat
  // mid-creation must never vanish). Replacing a foreign ACTIVE tab does NOT
  // mirror the URL (the stale foreign `?chat=` is cleared by the brand-gate
  // effect above).
  //
  // The owning-brand lookup (`chatBrandMap`) is owned by `<ForeignTabPruner>`,
  // which calls this — wrapped in an error boundary — so a query failure (e.g.
  // a deploy-skew window where the function isn't live yet) can NEVER crash the
  // chat. Pruning is a non-critical self-heal, not a render dependency.
  const pruneTabs = useCallback(
    (brandMap: Record<string, string>) => {
      if (!brandId) {
        return
      }
      const result = pruneForeignTabs({
        tabs: openTabsRef.current,
        brandMap,
        brandId,
        activeChatId,
        generateChatId: () => mintChatId({ orgId, brandId }),
      })
      if (!result.changed) {
        return
      }
      persistTabs(result.tabs)
      if (result.nextActiveId !== null) {
        setActiveChatIdLocal(result.nextActiveId)
      }
    },
    [orgId, brandId, activeChatId, persistTabs, setActiveChatIdLocal]
  )

  const setIsLeftPanelOpen = useCallback((open: boolean) => {
    setIsLeftPanelOpenRaw(open)
    if (typeof window === 'undefined') {
      return
    }
    try {
      localStorage.setItem(PANEL_OPEN_KEY, String(open))
    } catch {
      // ignore
    }
  }, [])

  // Tab-strip click. An `isNew` placeholder stays ephemeral (local activation
  // only, no `?chat=` write): it has no persisted chat behind it, so the URL
  // mirror would both strand a phantom id on refresh and — via the adopt
  // effect — promote the tab out of its "New chat" state.
  const selectTab = useCallback(
    (chatId: string) => {
      const tab = openTabsRef.current.find((t) => t.chatId === chatId)
      if (tab?.isNew) {
        setActiveChatIdLocal(chatId)
        return
      }
      setActiveChatId(chatId)
    },
    [setActiveChatId, setActiveChatIdLocal]
  )

  const addTab = useCallback(() => {
    const chatId = mintChatId({ orgId, brandId })
    const newTab: CanvasChatTab = { chatId, isNew: true }
    // Cap the strip — the just-added tab is active so it is never evicted.
    const next = capTabs([...openTabsRef.current, newTab], chatId)
    persistTabs(next)
    // Ephemeral isNew tab — keep URL pointing at whatever real chat the
    // user came from (or empty). markTabActive will mirror the URL on
    // first submit when the chat becomes "real".
    setActiveChatIdLocal(chatId)
    setIsLeftPanelOpen(true)
    return chatId
  }, [orgId, brandId, persistTabs, setActiveChatIdLocal, setIsLeftPanelOpen])

  const openExistingChat = useCallback(
    (chatId: string) => {
      // Adopt the chat and consume the active empty `isNew` placeholder, so
      // picking a chat from history while sitting on the fresh "New chat"
      // seed replaces the seed instead of orphaning it.
      const result = adoptTabInto({
        tabs: openTabsRef.current,
        activeChatId,
        chatId,
      })
      if (result.changed) {
        persistTabs(result.tabs)
      }
      setActiveChatId(chatId)
      setIsLeftPanelOpen(true)
    },
    [activeChatId, persistTabs, setActiveChatId, setIsLeftPanelOpen]
  )

  // Adopt a chat as active without writing `?chat=` (the path already
  // encodes it on `/chat/<id>`). Adds the tab if missing. Guards against
  // redundant state writes so it's safe to call from a render effect.
  const adoptActiveChat = useCallback(
    (chatId: string) => {
      // Adopt the path-encoded chat AND drop the empty `isNew` placeholder the
      // user was sitting on (the mount/scope seed). This is the orphan-tab
      // fix: landing on `/chat/<id>` from a fresh "New chat" seed must not
      // leave the seed beside the real chat.
      const result = adoptTabInto({
        tabs: openTabsRef.current,
        activeChatId,
        chatId,
      })
      if (result.changed) {
        persistTabs(result.tabs)
      }
      setActiveChatIdLocal(chatId)
    },
    [activeChatId, persistTabs, setActiveChatIdLocal]
  )

  const closeTab = useCallback(
    (chatId: string) => {
      const tabs = openTabsRef.current
      const idx = tabs.findIndex((t) => t.chatId === chatId)
      if (idx === -1) {
        return
      }
      const remaining = tabs.filter((t) => t.chatId !== chatId)
      const wasActive = activeChatId === chatId
      if (remaining.length === 0) {
        const freshId = mintChatId({ orgId, brandId })
        const freshTab: CanvasChatTab = { chatId: freshId, isNew: true }
        persistTabs([freshTab])
        // Same ephemeral-isNew-tab rule as addTab: don't mirror to URL.
        setActiveChatIdLocal(freshId)
        return
      }
      persistTabs(remaining)
      if (wasActive) {
        const adjacentIdx = Math.min(idx, remaining.length - 1)
        setActiveChatId(remaining[adjacentIdx]!.chatId)
      }
    },
    [
      orgId,
      brandId,
      activeChatId,
      persistTabs,
      setActiveChatId,
      setActiveChatIdLocal,
    ]
  )

  const markTabActive = useCallback(
    (chatId: string) => {
      const next = openTabsRef.current.map((t) =>
        t.chatId === chatId ? { ...t, isNew: false } : t
      )
      persistTabs(next)
      // Promotion: the chat is now real (first user submit). Mirror it
      // into the URL so refresh / back navigation lands on this chat
      // instead of an empty fresh tab.
      void setUrlChatId(chatId)
    },
    [persistTabs, setUrlChatId]
  )

  const isChatOpen = useCallback(
    (chatId: string) => openTabsRef.current.some((t) => t.chatId === chatId),
    []
  )

  // Memoize the return shape. Without this, every render produces a
  // new object identity and every `useGlobalChat()` consumer (panel,
  // list nav) re-renders. Canvas layout values no longer depend on
  // this object — they take attachment APIs only. Each individual
  // field is already stable (state value or memoized callback).
  return useMemo(
    () => ({
      openTabs,
      activeChatId,
      setActiveChatId,
      selectTab,
      addTab,
      openExistingChat,
      closeTab,
      markTabActive,
      isChatOpen,
      adoptActiveChat,
      pruneTabs,
      isLeftPanelOpen,
      setIsLeftPanelOpen,
    }),
    [
      openTabs,
      activeChatId,
      setActiveChatId,
      selectTab,
      addTab,
      openExistingChat,
      closeTab,
      markTabActive,
      isChatOpen,
      adoptActiveChat,
      pruneTabs,
      isLeftPanelOpen,
      setIsLeftPanelOpen,
    ]
  )
}
